import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  generateOpportunityPnl,
  generateOpportunityPnlErrorMessage,
} from '../lib/generateOpportunityPnl';
import { saveBusinessOpportunity } from '../lib/saveBusinessOpportunity';
import { newId } from '../utils/id';
import { useAppData } from './AppDataContext';
import { useUser } from './UserContext';

export type PnlGenerationJob = {
  id: string;
  opportunityId: string;
  opportunityName: string;
  status: 'loading' | 'error';
  error: string | null;
};

type OpportunityPnlGenerationContextValue = {
  jobs: PnlGenerationJob[];
  isGenerating: (opportunityId: string) => boolean;
  getJobError: (opportunityId: string) => string | null;
  startPnlGeneration: (opportunityId: string) => Promise<void>;
  dismissError: (jobId: string) => void;
};

const OpportunityPnlGenerationContext = createContext<
  OpportunityPnlGenerationContextValue | undefined
>(undefined);

export function OpportunityPnlGenerationProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { businessOpportunities } = useAppData();
  const opportunitiesRef = useRef(businessOpportunities);
  opportunitiesRef.current = businessOpportunities;

  const [jobs, setJobs] = useState<PnlGenerationJob[]>([]);
  const runningIdsRef = useRef(new Set<string>());

  const isGenerating = useCallback(
    (opportunityId: string) =>
      jobs.some((job) => job.opportunityId === opportunityId && job.status === 'loading'),
    [jobs]
  );

  const getJobError = useCallback(
    (opportunityId: string) => {
      const job = jobs.find((entry) => entry.opportunityId === opportunityId && entry.status === 'error');
      return job?.error ?? null;
    },
    [jobs]
  );

  const dismissError = useCallback((jobId: string) => {
    setJobs((current) => current.filter((job) => job.id !== jobId));
  }, []);

  const startPnlGeneration = useCallback(
    async (opportunityId: string) => {
      const trimmedId = opportunityId.trim();
      if (!trimmedId) return;
      if (!userId) return;
      if (runningIdsRef.current.has(trimmedId)) return;

      const opportunity = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
      const opportunityName = opportunity?.name?.trim() || 'Opportunity';

      runningIdsRef.current.add(trimmedId);
      const jobId = newId();
      setJobs((current) => [
        ...current.filter(
          (job) => !(job.opportunityId === trimmedId && job.status === 'error')
        ),
        {
          id: jobId,
          opportunityId: trimmedId,
          opportunityName,
          status: 'loading',
          error: null,
        },
      ]);

      try {
        const pnl = await generateOpportunityPnl(trimmedId);
        const current = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
        if (current) {
          await saveBusinessOpportunity(userId, {
            id: current.id,
            name: current.name,
            industry: current.industry,
            ideaSummary: current.ideaSummary,
            status: current.status,
            ideaIntelligence: current.ideaIntelligence,
            refinements: current.refinements,
            feedback: current.feedback,
            pnl,
            promotedCaseId: current.promotedCaseId,
            existing: current,
          });
        }
        setJobs((currentJobs) => currentJobs.filter((job) => job.id !== jobId));
      } catch (err) {
        const message = generateOpportunityPnlErrorMessage(err);
        setJobs((currentJobs) =>
          currentJobs.map((job) =>
            job.id === jobId ? { ...job, status: 'error', error: message } : job
          )
        );
      } finally {
        runningIdsRef.current.delete(trimmedId);
      }
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      jobs,
      isGenerating,
      getJobError,
      startPnlGeneration,
      dismissError,
    }),
    [jobs, isGenerating, getJobError, startPnlGeneration, dismissError]
  );

  return (
    <OpportunityPnlGenerationContext.Provider value={value}>
      {children}
    </OpportunityPnlGenerationContext.Provider>
  );
}

export function useOpportunityPnlGeneration(): OpportunityPnlGenerationContextValue {
  const ctx = useContext(OpportunityPnlGenerationContext);
  if (!ctx) {
    throw new Error(
      'useOpportunityPnlGeneration must be used within OpportunityPnlGenerationProvider'
    );
  }
  return ctx;
}
